import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

// Company logo as base64 (will be loaded dynamically)
let companyLogoBase64 = null;

/**
 * Load company logo as base64 for PDF embedding
 */
const loadCompanyLogo = async () => {
  if (companyLogoBase64) return companyLogoBase64;
  
  try {
    // Try to load the logo from public folder
    const logoUrl = `${window.location.origin}/customer-assets/Ezy_Mobility_Logo_New.png`;
    const response = await fetch(logoUrl);
    
    if (!response.ok) {
      console.error('Logo fetch failed:', response.status);
      return null;
    }
    
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        companyLogoBase64 = reader.result;
        resolve(companyLogoBase64);
      };
      reader.onerror = () => {
        console.error('FileReader error');
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load company logo:', error);
    return null;
  }
};

/**
 * Helper function to load image and convert to base64
 */
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Load image URL and convert to base64 data URL
 * Handles CORS issues by using multiple fallback methods including a backend proxy
 */
const loadImageAsBase64 = async (url) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  
  try {
    // Handle already encoded images
    if (url.startsWith('data:')) {
      return url;
    }
    
    // Method 1: Try direct fetch first (works for same-origin URLs)
    try {
      const response = await fetch(url, { 
        mode: 'cors',
        credentials: 'omit'
      });
      if (response.ok) {
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    } catch (fetchError) {
      console.log('Direct fetch failed, trying proxy...');
    }
    
    // Method 2: Use backend proxy to bypass CORS (best for OneDrive/external URLs)
    try {
      const token = localStorage.getItem('token');
      const proxyUrl = `${API}/api/proxy-image?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }
    } catch (proxyError) {
      console.log('Proxy method failed:', proxyError.message);
    }
    
    // Method 3: Try using an Image element with crossOrigin
    try {
      const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = url;
      });
      
      // Convert to base64 using canvas
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (imgError) {
      console.log('Image element method failed');
    }
    
    throw new Error('All image loading methods failed');
  } catch (error) {
    console.error('Failed to load image:', url, error);
    throw error;
  }
};

/**
 * Generate header for handover/takeover PDF with company logo only
 */
const addPDFHeader = async (doc, title) => {
  const pageWidth = doc.internal.pageSize.width;
  let yPos = 12;

  // Try to add company logo (larger size, no text)
  try {
    const logoBase64 = await loadCompanyLogo();
    if (logoBase64) {
      // Add logo centered at top - larger size
      const logoWidth = 70;
      const logoHeight = 28;
      doc.addImage(logoBase64, 'PNG', (pageWidth - logoWidth) / 2, yPos, logoWidth, logoHeight);
      yPos += logoHeight + 8;
    } else {
      // Fallback to text if logo fails
      doc.setFontSize(24);
      doc.setTextColor(139, 195, 74);
      doc.setFont(undefined, 'bold');
      doc.text('EZY MOBILITY', pageWidth / 2, yPos + 15, { align: 'center' });
      yPos += 25;
    }
  } catch (error) {
    // Fallback to text header
    doc.setFontSize(24);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('EZY MOBILITY', pageWidth / 2, yPos + 15, { align: 'center' });
    yPos += 25;
  }
  
  // Green line separator
  doc.setLineWidth(0.5);
  doc.setDrawColor(139, 195, 74);
  doc.line(20, yPos, pageWidth - 20, yPos);
  
  // Document Title
  yPos += 10;
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text(title, pageWidth / 2, yPos, { align: 'center' });
  
  return yPos + 10;
};

/**
 * Convert any image URL / data URL to a PNG data URL.
 *
 * Uses `fetch + Blob + FileReader` instead of the classic `<img>` + canvas
 * trick because:
 *  - `<img>` with `crossOrigin = 'anonymous'` taints the canvas when the
 *    server omits CORS headers — `canvas.toDataURL()` then throws a
 *    SecurityError, which was silently swallowed by the caller and rendered
 *    "(signature unavailable)" / "(fingerprint unavailable)" on the PDF.
 *  - Some browsers can render BMP in `<img>` but the canvas+toDataURL path
 *    still fails on tainted canvases.
 *
 * For BMP fingerprint images we additionally re-encode through a canvas so
 * jsPDF (which only understands PNG/JPEG) can embed them.
 */
const toPngDataUrl = async (input) => {
  if (!input) return null;

  // Already a usable PNG/JPEG data URL — no conversion needed.
  if (typeof input === 'string' && /^data:image\/(png|jpeg|jpg);base64,/i.test(input)) {
    return input;
  }

  // Fetch the bytes (same-origin or with proper CORS) and read as a data URL.
  let dataUrl;
  try {
    const res = await fetch(input, { credentials: 'omit', cache: 'force-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    throw new Error(`Failed to fetch biometric image: ${e.message}`);
  }

  // If it's already PNG/JPEG return as-is. Otherwise (BMP/GIF/etc) re-encode
  // through a canvas to PNG so jsPDF can consume it.
  if (/^data:image\/(png|jpeg|jpg);base64,/i.test(dataUrl)) {
    return dataUrl;
  }
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width || 300;
        canvas.height = img.naturalHeight || img.height || 200;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) { reject(err); }
    };
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;   // already a data URL — same-origin, no tainting
  });
};

/**
 * Render a side-by-side "Rider Acknowledgement" block with the captured
 * signature on the left and thumb print on the right. Returns updated yPos.
 */
const addBiometricBlock = async (doc, record, yPos) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  const sigUrl = record.rider_signature_url || null;
  const fpUrl = record.rider_fingerprint_url || null;
  if (!sigUrl && !fpUrl) return yPos;

  // Reserve enough space for the block; new page if needed.
  if (yPos > pageHeight - 80) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(60, 60, 60);
  doc.text('RIDER ACKNOWLEDGEMENT', 20, yPos);
  yPos += 4;

  const colW = (pageWidth - 50) / 2;
  const boxH = 40;
  const sigX = 20;
  const fpX = sigX + colW + 10;
  const boxY = yPos + 2;

  // Signature box
  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.3);
  doc.rect(sigX, boxY, colW, boxH);
  doc.setFontSize(6);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text('Signature', sigX + 1, boxY - 0.5);
  if (sigUrl) {
    try {
      const url = sigUrl.startsWith('http') ? sigUrl : `${process.env.REACT_APP_BACKEND_URL}${sigUrl}`;
      const data = await toPngDataUrl(url);
      doc.addImage(data, 'PNG', sigX + 2, boxY + 2, colW - 4, boxH - 4);
    } catch (e) {
      doc.text('(signature unavailable)', sigX + 2, boxY + boxH / 2);
    }
  } else {
    doc.text('(not captured)', sigX + 2, boxY + boxH / 2);
  }

  // Thumb print box
  doc.rect(fpX, boxY, colW, boxH);
  doc.setFontSize(6);
  doc.setTextColor(120, 120, 120);
  doc.text('Thumb Print', fpX + 1, boxY - 0.5);
  if (fpUrl) {
    try {
      const url = fpUrl.startsWith('http') ? fpUrl : `${process.env.REACT_APP_BACKEND_URL}${fpUrl}`;
      const data = await toPngDataUrl(url);
      // Center the fingerprint within its box, keep portrait aspect
      const imgH = boxH - 4;
      const imgW = imgH * 0.78; // typical aspect for HU20 capture
      doc.addImage(data, 'PNG', fpX + (colW - imgW) / 2, boxY + 2, imgW, imgH);
    } catch (e) {
      doc.text('(fingerprint unavailable)', fpX + 2, boxY + boxH / 2);
    }
  } else {
    doc.text('(not captured)', fpX + 2, boxY + boxH / 2);
  }

  yPos = boxY + boxH + 3;

  // Capture metadata
  doc.setFontSize(6);
  doc.setTextColor(80, 80, 80);
  const ts = record.biometrics_captured_at
    ? new Date(record.biometrics_captured_at).toLocaleString()
    : new Date().toLocaleString();
  doc.text(`Captured: ${ts}`, sigX, yPos);
  if (record.rider_fingerprint_device_serial) {
    doc.text(`Scanner: SecuGen HU20  S/N ${record.rider_fingerprint_device_serial}`, sigX + colW + 10, yPos);
  }
  if (record.rider_fingerprint_quality != null) {
    yPos += 3;
    doc.text(`Quality: ${record.rider_fingerprint_quality} / 100`, sigX + colW + 10, yPos);
  }
  return yPos + 4;
};

/**
 * Add footer to all pages
 */
const addPDFFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const pageCount = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(
      'Ezy Mobility - Electric Motorcycle Fleet Management',
      pageWidth / 2,
      pageHeight - 5,
      { align: 'center' }
    );
  }
};

/**
 * Resolve photo URLs to base64 via the backend (handles OneDrive auth, CORS, etc.)
 */
const resolvePhotosViaBackend = async (urls) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API}/api/resolve-photos`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ urls })
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.photos || [];
    }
  } catch (error) {
    console.error('Failed to resolve photos via backend:', error);
  }
  return urls.map(() => null);
};

/**
 * Add photos section to PDF in a grid layout (2 columns for larger, clearer photos)
 */
const addPhotosSection = async (doc, photos, title, yPos) => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Filter out empty/null photos
  const validPhotos = photos?.filter(p => p && (typeof p === 'string') && p.trim() !== '') || [];
  
  // Always show the section title
  if (yPos > pageHeight - 100) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, 20, yPos);
  yPos += 12;
  
  if (validPhotos.length > 0) {
    // Resolve all photo URLs to base64 via backend (handles OneDrive, CORS, etc.)
    let resolvedPhotos;
    try {
      resolvedPhotos = await resolvePhotosViaBackend(validPhotos);
    } catch (e) {
      console.error('Backend photo resolution failed:', e);
      resolvedPhotos = validPhotos.map(() => null);
    }
    
    // Grid layout: 2 columns for larger, clearer photos
    const cols = 2;
    const gap = 10;
    const imgWidth = (pageWidth - 40 - gap) / cols;
    const imgHeight = imgWidth * 0.75;
    let col = 0;
    
    for (let i = 0; i < validPhotos.length; i++) {
      const xPos = 20 + (col * (imgWidth + gap));
      
      // Check if we need a new page
      if (yPos + imgHeight > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
        col = 0;
      }
      
      try {
        // Use backend-resolved base64 first
        let imgData = resolvedPhotos[i];
        
        // If backend resolution failed, try direct loading as fallback
        if (!imgData) {
          try {
            imgData = await loadImageAsBase64(validPhotos[i]);
          } catch (loadError) {
            console.error(`Fallback load also failed for photo ${i + 1}`);
          }
        }
        
        if (imgData) {
          doc.addImage(imgData, 'JPEG', xPos, yPos, imgWidth, imgHeight);
          
          // Add border
          doc.setDrawColor(180, 180, 180);
          doc.setLineWidth(0.5);
          doc.rect(xPos, yPos, imgWidth, imgHeight);
          
          // Add photo number label
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          doc.text(`Photo ${i + 1}`, xPos + 2, yPos + imgHeight + 6);
        } else {
          throw new Error('No image data available');
        }
        
        col++;
        if (col >= cols) {
          col = 0;
          yPos += imgHeight + 14;
        }
      } catch (error) {
        console.error(`Error loading photo ${i + 1}:`, error);
        
        // Draw placeholder box
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(245, 245, 245);
        doc.rect(xPos, yPos, imgWidth, imgHeight, 'FD');
        
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text(`Photo ${i + 1}`, xPos + imgWidth/2, yPos + imgHeight/2, { align: 'center' });
        doc.setFontSize(8);
        doc.text('(unavailable)', xPos + imgWidth/2, yPos + imgHeight/2 + 10, { align: 'center' });
        
        col++;
        if (col >= cols) {
          col = 0;
          yPos += imgHeight + 14;
        }
      }
    }
    
    // Adjust yPos if last row wasn't complete
    if (col > 0) {
      yPos += imgHeight + 14;
    }
  } else {
    // No photos - show message
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.setFont(undefined, 'italic');
    doc.text('No photos attached', 20, yPos);
    yPos += 15;
  }
  
  return yPos;
};

/**
 * Produce a human-friendly certificate number from the underlying record.
 * Easy to read out over the phone / email to a customer, e.g.
 *
 *   HO-20260622-1758-DXB228442
 *      ^date    ^time ^bike plate (alphanumeric only)
 *
 * Falls back gracefully if any field is missing. Backed up with the last
 * 4 chars of the database UUID for absolute uniqueness in case two handovers
 * share the exact same date/time/plate.
 */
const formatCertificateNumber = (record, kind /* 'HO' | 'TO' */) => {
  const date = (record[`${kind === 'HO' ? 'handover' : 'takeover'}_date`] || '').replace(/-/g, '').slice(0, 8);
  const time = (record[`${kind === 'HO' ? 'handover' : 'takeover'}_time`] || '').replace(/:/g, '').slice(0, 4);
  const plate = (record.bike_plate_number || record.plate_number || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const tail = (record.id || '').slice(-4).toUpperCase();
  const parts = [kind, date, time, plate].filter(Boolean);
  return tail ? `${parts.join('-')}-${tail}` : parts.join('-');
};



/**
 * Generate PDF for Motorcycle Handover
 */
export const generateHandoverPDF = async (handover, motorcycle = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Add header (async to load logo)
  let yPos = await addPDFHeader(doc, 'MOTORCYCLE HANDOVER CERTIFICATE');
  
  // Record ID and Date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`Certificate No: ${formatCertificateNumber(handover, 'HO')}`, 20, yPos);
  doc.text(`Date: ${handover.handover_date}`, pageWidth - 20, yPos, { align: 'right' });
  
  yPos += 8;
  doc.text(`Time: ${handover.handover_time}`, pageWidth - 20, yPos, { align: 'right' });
  
  // Motorcycle Information
  yPos += 12;
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MOTORCYCLE DETAILS', 20, yPos);
  
  yPos += 8;
  const motorcycleInfo = [
    ['Plate Number', handover.bike_plate_number || handover.motorcycle_plate || 'N/A'],
    ['Model', motorcycle?.model_name || 'N/A'],
    ['Chassis Number', motorcycle?.chassis_number || 'N/A'],
    ['Color', motorcycle?.color || 'N/A'],
    ['Status', motorcycle?.status || 'N/A']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: motorcycleInfo,
    theme: 'striped',
    headStyles: { fillColor: [139, 195, 74] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 20, right: 20 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // Handover Details
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('HANDOVER INFORMATION', 20, yPos);
  
  yPos += 8;
  const handoverDetails = [
    ['Lessee Company', handover.lessee_name || 'N/A'],
    ['Location', handover.location || handover.location_name || 'N/A'],
    ['Technician Name', handover.technician_name || 'N/A'],
    ['Rider Name', handover.rider_name || 'N/A'],
    ['Rider Emirates ID', handover.rider_emirates_id || 'N/A']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: handoverDetails,
    theme: 'striped',
    headStyles: { fillColor: [139, 195, 74] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 20, right: 20 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // Notes
  if (handover.notes) {
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('NOTES', 20, yPos);
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const splitNotes = doc.splitTextToSize(handover.notes, pageWidth - 40);
    doc.text(splitNotes, 20, yPos);
    yPos += splitNotes.length * 5 + 10;
  }
  
  // Handover Photos
  yPos = await addPhotosSection(doc, handover.handover_photos, 'HANDOVER PHOTOS', yPos);

  // Signature + Thumb-print block
  yPos = await addBiometricBlock(doc, handover, yPos);

  // Signature Section
  // Legal Terms & Conditions Section (Micro Print)
  // Check if we need a new page for the terms
  if (yPos > pageHeight - 100) {
    doc.addPage();
    yPos = 20;
  }
  
  yPos += 10;
  
  // Terms header
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(60, 60, 60);
  doc.text('TERMS & CONDITIONS OF MOTORCYCLE HANDOVER', 20, yPos);
  
  yPos += 6;
  
  // Legal disclaimer text (micro print)
  const legalText = `By taking possession of the motorcycle provided by Ezy Mobility Solutions Motorcycle Rental L.L.C. ("Company"), the Rider (Delivery Associate) irrevocably acknowledges and agrees that: the motorcycle is received in usable condition; it shall be used strictly for official work purposes only; any personal, non-official, negligent, or unauthorized use, including lending, sub-leasing, transfer, or third-party operation, is strictly prohibited and shall constitute misuse. The Rider accepts full personal responsibility for all traffic fines, penalties, black points, impound charges, damages, losses, accidents, theft, seizure, or legal consequences arising during possession or use of the motorcycle and authorizes the Company to recover all related costs by any lawful means. The Rider shall operate the motorcycle in compliance with UAE laws, maintain it in good condition, present it for maintenance or inspection as instructed, and shall not carry out or permit any repair, modification, or alteration without prior written approval of the Company. Any damage or loss arising from misuse, negligence, rash riding, overloading, or violation of instructions shall be borne entirely by the Rider, including repair costs, depreciation, downtime, insurance excess, and recovery expenses. Handover of the motorcycle does not waive or replace any obligations of the Lessee under the Motorcycle Lease Agreement, all of which remain valid and enforceable. The Rider's digital signature and biometric thumb print captured on this certificate are recorded as the Rider's express, informed and unconditional acceptance of all the above terms. These captured biometrics, together with the Rider's possession, custody and use of the motorcycle, shall constitute a legally binding electronic acknowledgment under the UAE Electronic Transactions and Trust Services Law (Federal Decree-Law No. 46 of 2021) and shall be enforceable under the laws of the United Arab Emirates, with UAE courts having exclusive jurisdiction.`;
  
  doc.setFontSize(6);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  
  // Split the legal text to fit within page width
  const splitLegalText = doc.splitTextToSize(legalText, pageWidth - 40);
  doc.text(splitLegalText, 20, yPos);
  yPos += splitLegalText.length * 2.5 + 8;
  
  // Acknowledgment section
  doc.setFontSize(7);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Acknowledged by:', 20, yPos);
  
  yPos += 5;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(7);
  doc.text(`Rider Name: ${handover.rider_name || 'N/A'}`, 20, yPos);
  yPos += 4;
  doc.text(`Lessee Name: ${handover.lessee_name || 'N/A'}`, 20, yPos);
  yPos += 4;
  doc.text(`Rider Emirates ID No.: ${handover.rider_emirates_id || 'N/A'}`, 20, yPos);
  
  // Add footer
  addPDFFooter(doc);
  
  // Save PDF
  const plateNumber = handover.bike_plate_number || handover.motorcycle_plate || 'Unknown';
  const fileName = `Handover_${plateNumber}_${handover.handover_date}.pdf`;
  doc.save(fileName);
};

/**
 * Generate PDF for Motorcycle Takeover
 */
export const generateTakeoverPDF = async (takeover, motorcycle = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Add header (async to load logo)
  let yPos = await addPDFHeader(doc, 'MOTORCYCLE TAKEOVER CERTIFICATE');
  
  // Record ID and Date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`Certificate No: ${formatCertificateNumber(takeover, 'TO')}`, 20, yPos);
  doc.text(`Date: ${takeover.takeover_date}`, pageWidth - 20, yPos, { align: 'right' });
  
  yPos += 8;
  doc.text(`Time: ${takeover.takeover_time}`, pageWidth - 20, yPos, { align: 'right' });
  
  // Motorcycle Information
  yPos += 12;
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MOTORCYCLE DETAILS', 20, yPos);
  
  yPos += 8;
  const motorcycleInfo = [
    ['Plate Number', takeover.bike_plate_number || takeover.motorcycle_plate || 'N/A'],
    ['Model', motorcycle?.model_name || 'N/A'],
    ['Chassis Number', motorcycle?.chassis_number || 'N/A'],
    ['Color', motorcycle?.color || 'N/A'],
    ['Status', motorcycle?.status || 'N/A']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: motorcycleInfo,
    theme: 'striped',
    headStyles: { fillColor: [139, 195, 74] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 20, right: 20 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // Takeover Details
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('TAKEOVER INFORMATION', 20, yPos);
  
  yPos += 8;
  const takeoverDetails = [
    ['Lessee Company', takeover.lessee_name || 'N/A'],
    ['Location', takeover.location || takeover.location_name || 'N/A'],
    ['Technician Name', takeover.technician_name || 'N/A'],
    ['Rider Name', takeover.rider_name || 'N/A'],
    ['Rider Emirates ID', takeover.rider_emirates_id || 'N/A'],
    ['Takeover Type', takeover.temporary_submission ? 'TEMPORARY' : 'PERMANENT'],
    ['Reason', takeover.reason || takeover.temporary_reason || 'N/A']
  ];
  
  // Add temporary dates if applicable
  if (takeover.temporary_submission) {
    takeoverDetails.push(
      ['Expected Return Date', takeover.expected_return_date || takeover.temporary_end_date || 'N/A']
    );
  }
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: takeoverDetails,
    theme: 'striped',
    headStyles: { fillColor: [139, 195, 74] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 20, right: 20 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // Notes
  if (takeover.notes) {
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('NOTES', 20, yPos);
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const splitNotes = doc.splitTextToSize(takeover.notes, pageWidth - 40);
    doc.text(splitNotes, 20, yPos);
    yPos += splitNotes.length * 5 + 10;
  }
  
  // Takeover Photos
  yPos = await addPhotosSection(doc, takeover.takeover_photos, 'TAKEOVER PHOTOS', yPos);

  // Signature + Thumb-print block
  yPos = await addBiometricBlock(doc, takeover, yPos);

  // Legal Terms & Conditions Section (Micro Print)
  // Check if we need a new page for the terms
  if (yPos > pageHeight - 100) {
    doc.addPage();
    yPos = 20;
  }
  
  yPos += 10;
  
  // Terms header
  doc.setFontSize(8);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(60, 60, 60);
  doc.text('TERMS & CONDITIONS OF MOTORCYCLE TAKEOVER', 20, yPos);
  
  yPos += 6;
  
  // Legal disclaimer text (micro print)
  const legalText = `The physical take-over or submission of the motorcycle to Ezy Mobility Solutions Motorcycle Rental L.L.C. ("Company") shall not constitute or imply final inspection, clearance, settlement, waiver, closure, or termination of any liabilities or of the Motorcycle Lease Agreement executed between the Company and the Lessee, all of which shall remain valid and enforceable; the Company expressly reserves the right to assess, record, claim, and recover at any time any traffic fines, penalties, black points, impound charges, damages, accidental losses, third-party claims, insurance excess, depreciation, administrative costs, or other expenses arising during the period the motorcycle was in the possession, custody, or control of the Rider and/or the Lessee and exceeding the lease amount, whether identified before or after take-over; the Rider and/or the Lessee shall attend, cooperate with, and comply with any summons, notices, proceedings, or requirements of traffic authorities, courts, police, insurers, platforms, or third parties, including providing documents, confirmations, or electronic verifications as required; the Lessee expressly acknowledges that the Rider acted as its authorized legal representative in relation to the motorcycle, and that any acknowledgments, admissions, confirmations, or statements made by the Rider regarding usage, violations, damages, losses, or costs shall be binding on the Lessee and payable by the Lessee to the Company without dispute; by submitting or returning the motorcycle, the Rider and the Lessee unconditionally accept and agree to the terms of this take-over. The Rider's digital signature and biometric thumb print captured on this certificate are recorded as the Rider's express, informed and unconditional acceptance of all the above terms and shall constitute a legally binding electronic acknowledgment under the UAE Electronic Transactions and Trust Services Law (Federal Decree-Law No. 46 of 2021), enforceable under the laws of the United Arab Emirates and subject to the exclusive jurisdiction of UAE courts.`;
  
  doc.setFontSize(6);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  
  // Split the legal text to fit within page width
  const splitLegalText = doc.splitTextToSize(legalText, pageWidth - 40);
  doc.text(splitLegalText, 20, yPos);
  yPos += splitLegalText.length * 2.5 + 8;
  
  // Acknowledgment section
  doc.setFontSize(7);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Acknowledged by:', 20, yPos);
  
  yPos += 5;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(7);
  doc.text(`Rider Name: ${takeover.rider_name || 'N/A'}`, 20, yPos);
  yPos += 4;
  doc.text(`Lessee Name: ${takeover.lessee_name || 'N/A'}`, 20, yPos);
  yPos += 4;
  doc.text(`Rider Emirates ID No.: ${takeover.rider_emirates_id || 'N/A'}`, 20, yPos);
  
  // Add footer
  addPDFFooter(doc);
  
  // Save PDF
  const plateNumber = takeover.bike_plate_number || takeover.motorcycle_plate || 'Unknown';
  const fileName = `Takeover_${plateNumber}_${takeover.takeover_date}.pdf`;
  doc.save(fileName);
};

/**
 * Generate PDF for Accident Report
 */
export const generateAccidentPDF = async (accident, motorcycle = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Add header (async to load logo)
  let yPos = await addPDFHeader(doc, 'MOTORCYCLE ACCIDENT REPORT');
  
  // Record ID and Date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`Report ID: ${accident.id}`, 20, yPos);
  doc.text(`Date: ${accident.accident_date}`, pageWidth - 20, yPos, { align: 'right' });
  
  yPos += 8;
  doc.text(`Time: ${accident.accident_time}`, pageWidth - 20, yPos, { align: 'right' });
  
  // Motorcycle Information
  yPos += 12;
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('MOTORCYCLE DETAILS', 20, yPos);
  
  yPos += 8;
  const motorcycleInfo = [
    ['Plate Number', accident.bike_plate_number || accident.motorcycle_plate || 'N/A'],
    ['Model', motorcycle?.model_name || 'N/A'],
    ['Chassis Number', motorcycle?.chassis_number || 'N/A'],
    ['Color', motorcycle?.color || 'N/A'],
    ['Lessee', accident.lessee_name || motorcycle?.lessee_name || 'N/A']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: motorcycleInfo,
    theme: 'striped',
    headStyles: { fillColor: [220, 53, 69] }, // Red for accident
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 20, right: 20 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // Accident Details
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text('ACCIDENT INFORMATION', 20, yPos);
  
  yPos += 8;
  const accidentDetails = [
    ['Rider Name', accident.rider_name || 'N/A'],
    ['Accident Location', accident.location || accident.location_name || 'N/A'],
    ['Police Report Type', accident.police_report_type === 'green' ? 'Green (Minor)' : 'Red (Major)'],
    ['Estimated Damage Cost', accident.estimated_damage_cost ? `AED ${accident.estimated_damage_cost}` : 'N/A'],
    ['Insurance Claim', accident.submit_insurance_claim ? 'Yes' : 'No']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: accidentDetails,
    theme: 'striped',
    headStyles: { fillColor: [220, 53, 69] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 60 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 20, right: 20 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // Description
  if (accident.description) {
    if (yPos > pageHeight - 50) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('ACCIDENT DESCRIPTION', 20, yPos);
    
    yPos += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const splitDesc = doc.splitTextToSize(accident.description, pageWidth - 40);
    doc.text(splitDesc, 20, yPos);
    yPos += splitDesc.length * 5 + 10;
  }
  
  // Police Report Photos
  if (accident.police_report_photos && accident.police_report_photos.length > 0) {
    yPos = await addPhotosSection(doc, accident.police_report_photos, 'POLICE REPORT DOCUMENTS', yPos);
  }
  
  // Bike Damage Photos
  if (accident.bike_photos && accident.bike_photos.length > 0) {
    yPos = await addPhotosSection(doc, accident.bike_photos, 'BIKE DAMAGE PHOTOS', yPos);
  }
  
  // Add footer
  addPDFFooter(doc);
  
  // Save PDF
  const plateNumber = accident.bike_plate_number || accident.motorcycle_plate || 'Unknown';
  const fileName = `Accident_Report_${plateNumber}_${accident.accident_date}.pdf`;
  doc.save(fileName);
};
